import Debug from '../utils/Debug.js';

/**
 * Room listing service via serverless proxy (/api/rooms).
 * Handles room creation, discovery, heartbeat, and cleanup.
 * Firebase URL stays server-side — client only calls /api/rooms.
 */
export default class RoomService {
  static HEARTBEAT_INTERVAL = 45000; // 45s

  constructor() {
    this._heartbeatInterval = null;
    this._currentRoomId = null;
    this._playerCount = 1;
  }

  /**
   * Register a room in the public listing.
   * @param {string} roomId
   * @param {Object} options
   * @param {string} [options.name] - Display name
   * @param {number} [options.maxPlayers=8]
   * @param {boolean} [options.isPublic=true]
   * @returns {Promise<Object>} Room data
   */
  async createRoom(roomId, options = {}) {
    const data = {
      name: options.name || roomId,
      playerCount: 1,
      maxPlayers: options.maxPlayers || 8,
      isPublic: options.isPublic !== false,
      hasPassword: !!options.hasPassword,
      createdAt: Date.now(),
      lastHeartbeat: Date.now()
    };
    if (options.passwordHash) data.passwordHash = options.passwordHash;

    await fetch('/api/rooms', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'create', roomId, data })
    });

    this._currentRoomId = roomId;
    this._startHeartbeat(roomId);
    return data;
  }

  /**
   * Join an existing room's heartbeat cycle without overwriting its Firebase data.
   * Used when joining a room that already exists in the DB.
   * @param {string} roomId
   */
  joinExistingRoom(roomId) {
    this._currentRoomId = roomId;
    this._startHeartbeat(roomId);
  }

  /**
   * List all active public rooms.
   * Server filters stale rooms and sorts by player count.
   * @returns {Promise<Array>} Array of { id, name, playerCount, maxPlayers, createdAt }
   */
  async listRooms() {
    try {
      const res = await fetch('/api/rooms');
      if (!res.ok) {
        Debug.warn('RoomService', `Room list request failed (${res.status})`);
        return [];
      }
      const data = await res.json();
      if (!Array.isArray(data)) {
        Debug.warn('RoomService', 'Unexpected room list response:', data);
        return [];
      }
      return data;
    } catch (err) {
      Debug.warn('RoomService', 'Failed to list rooms:', err);
      return [];
    }
  }

  /**
   * Check if a room exists in the public listing and get its metadata.
   * @param {string} roomId
   * @returns {Promise<{exists: boolean, hasPassword?: boolean}>}
   */
  async roomExists(roomId) {
    try {
      const res = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'exists', roomId })
      });
      return await res.json(); // { exists, hasPassword }
    } catch {
      // If the check fails, assume room might exist (safe fallback)
      return { exists: true };
    }
  }

  /**
   * Verify a password hash against the stored hash for a room.
   * @param {string} roomId
   * @param {string} passwordHash - 16-char hex SHA-256 prefix
   * @returns {Promise<boolean>}
   */
  async verifyPassword(roomId, passwordHash) {
    try {
      const res = await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'verifyPassword', roomId, passwordHash })
      });
      const result = await res.json();
      return result.valid;
    } catch {
      return true; // Fallback: let them try to connect
    }
  }

  /**
   * Update the player count locally. The next heartbeat will
   * carry this value to Firebase, avoiding a separate write.
   * @param {string} _roomId - unused, kept for call-site compat
   * @param {number} count
   */
  updatePlayerCount(_roomId, count) {
    this._playerCount = count;
  }

  /**
   * Remove a room from the listing.
   * @param {string} roomId
   */
  async deleteRoom(roomId) {
    this._stopHeartbeat();
    try {
      await fetch('/api/rooms', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', roomId })
      });
    } catch (err) {
      Debug.warn('RoomService', 'Failed to delete room:', err);
    }
  }

  /**
   * Leave the current room (stop heartbeat, update player count).
   * When no peers remain, the heartbeat stops and the room naturally
   * expires via the 60s TTL — stale cleanup purges it from Firebase.
   * @param {number} remainingPeers
   */
  async leaveRoom(remainingPeers) {
    const roomId = this._currentRoomId;
    this._stopHeartbeat();
    this._currentRoomId = null;

    // Send final player count immediately since heartbeat is stopping
    if (roomId && remainingPeers > 0) {
      try {
        await fetch('/api/rooms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'updatePlayers', roomId, count: remainingPeers })
        });
      } catch (err) {
        Debug.warn('RoomService', 'Failed to send final player count:', err);
      }
    }
  }

  _startHeartbeat(roomId) {
    this._stopHeartbeat();
    this._heartbeatInterval = setInterval(async () => {
      try {
        await fetch('/api/rooms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'heartbeat', roomId, count: this._playerCount })
        });
      } catch (err) {
        // Silently ignore heartbeat failures
      }
    }, RoomService.HEARTBEAT_INTERVAL);
  }

  _stopHeartbeat() {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }
}
